import { normalizeJoinPolicy, VCS_JOIN_POLICY } from 'consts';
import { useSnackbar } from '@web-chat-core/ui-kit';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { roomInfoStore } from 'state/store';
import { selectLocalParticipant, selectRoomMetadata } from 'state/selectors';
import { isGuestParticipant } from 'useCase/moderation/status/moderators';
import { useConnectToRoom } from 'features/PreJoin/hooks/useConnectToRoom';
import {
    approveWaitingUsersApi,
    CallJoinPolicy,
    syncCallSettingsApi,
} from 'features/AccessRequests/api/accessRequestsApi';
import { getConferenceJwtToken } from 'features/AccessRequests/utils/getConferenceJwtToken';
import { markWaitingListHandled } from 'features/AccessRequests/utils/waitingListRegistry';

export interface ApplyCallSettingsData {
    joinPolicy: CallJoinPolicy;
    moderatorsApprovalOnly: boolean;
}

export const useApplyCallSettings = () => {
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { getTokenAndWssUrl } = useConnectToRoom();

    return useCallback(
        (data: ApplyCallSettingsData) => {
            const currentMetadata = selectRoomMetadata();
            if (!currentMetadata) return;

            const localParticipant = selectLocalParticipant();
            const isLocalGuest = localParticipant != null && isGuestParticipant(localParticipant);
            const getJwtToken = (): Promise<string | null> => getConferenceJwtToken(getTokenAndWssUrl);

            const previousJoinData = currentMetadata.joinApprovalData || {};
            const previousPolicy = previousJoinData.joinApprovalPolicy;
            const previousModeratorsApprovalOnly = previousJoinData.onlyModeratorsCanApproveWaitingUsers;
            const wasApprovalMode = normalizeJoinPolicy(previousPolicy) !== VCS_JOIN_POLICY.ALL;
            const nextServerPolicy = data.joinPolicy;
            const pendingWaitingUsers = previousJoinData.waitingUsers || [];
            // При переходе из режима с разрешением во "всех без разрешения"
            // ожидающие фактически попадают в комнату — гасим их локально и на сервере,
            // чтобы при возврате режима баннер не показывал тех, кто уже на звонке.
            const shouldFlushWaitingUsers =
                wasApprovalMode && nextServerPolicy === 'WITHOUT_RESTRICTIONS' && pendingWaitingUsers.length > 0;

            roomInfoStore.setMetadata({
                ...currentMetadata,
                joinApprovalData: {
                    ...previousJoinData,
                    joinApprovalPolicy: nextServerPolicy,
                    onlyModeratorsCanApproveWaitingUsers: data.moderatorsApprovalOnly,
                    ...(shouldFlushWaitingUsers && {
                        waitingUsers: [],
                        acceptedUsers: [...(previousJoinData.acceptedUsers || []), ...pendingWaitingUsers],
                    }),
                },
            });

            if (shouldFlushWaitingUsers) {
                markWaitingListHandled(
                    pendingWaitingUsers.map((u) => u.waitingListId),
                    {
                        hideFromMetadata: true,
                    },
                );
                void getJwtToken()
                    .then((jwtToken) =>
                        approveWaitingUsersApi({
                            waitingListIds: pendingWaitingUsers.map((u) => u.waitingListId),
                            operation: 'APPROVE',
                            jwtToken,
                            isLocalGuest,
                        }),
                    )
                    .then(({ success }) => {
                        if (success) return;

                        const latestMetadata = selectRoomMetadata();
                        if (latestMetadata) {
                            const latestJoinData = latestMetadata.joinApprovalData || {};
                            const flushedIds = new Set(pendingWaitingUsers.map((u) => u.waitingListId));
                            roomInfoStore.setMetadata({
                                ...latestMetadata,
                                joinApprovalData: {
                                    ...latestJoinData,
                                    waitingUsers: pendingWaitingUsers,
                                    acceptedUsers: (latestJoinData.acceptedUsers || []).filter(
                                        (u) => !flushedIds.has(u.waitingListId),
                                    ),
                                },
                            });
                        }

                        showSnackbar(t('web_videoconference_error'), {
                            type: 'error',
                            testId: 'snackbar-call-settings-flush-waiting-error',
                        });
                    });
            }

            void getJwtToken()
                .then((jwtToken) => syncCallSettingsApi({ ...data, jwtToken, isLocalGuest }))
                .then(({ failedFields }) => {
                    if (failedFields.length > 0) {
                        const latestMetadata = selectRoomMetadata();
                        if (latestMetadata) {
                            const latestJoinData = latestMetadata.joinApprovalData || {};
                            roomInfoStore.setMetadata({
                                ...latestMetadata,
                                joinApprovalData: {
                                    ...latestJoinData,
                                    ...(failedFields.includes('joinPolicy') && {
                                        joinApprovalPolicy: previousPolicy,
                                    }),
                                    ...(failedFields.includes('moderatorsApprovalOnly') && {
                                        onlyModeratorsCanApproveWaitingUsers: previousModeratorsApprovalOnly,
                                    }),
                                },
                            });
                        }
                    }

                    if (failedFields.includes('joinPolicy')) {
                        showSnackbar(t('web_videoconference_error'), {
                            type: 'error',
                            testId: 'snackbar-call-settings-join-policy-error',
                        });
                    }
                    if (failedFields.includes('moderatorsApprovalOnly')) {
                        showSnackbar(t('web_videoconference_error'), {
                            type: 'error',
                            testId: 'snackbar-call-settings-moderators-error',
                        });
                    }
                });
        },
        [showSnackbar, t, getTokenAndWssUrl],
    );
};
